import React, { useState } from 'react';
import { Mail, Shield, X, Check } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const AuthGate = ({ isOpen, onClose, onSuccess }) => {
  const { startSignIn } = useAuth();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setIsSubmitting(true);
    setError('');
    
    const result = await startSignIn(email.trim());
    
    setIsSubmitting(false);
    
    if (result.success) {
      setSuccess(true);
      if (onSuccess) {
        // Delay to show success state
        setTimeout(() => onSuccess(), 2000);
      }
    } else {
      setError(result.error);
    }
  };

  const handleClose = () => {
    setEmail('');
    setSuccess(false);
    setError('');
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div 
        className="bg-white rounded-2xl max-w-md w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ border: '1px solid #E5E7EB' }}
      >
        {/* Close button */}
        <div className="flex justify-end p-3">
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" style={{ color: '#6B7280' }} />
          </button>
        </div>

        <div className="px-6 pb-6">
          {/* Icon badge */}
          <div className="flex justify-center mb-4">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(0, 31, 63, 0.06)' }}
            >
              <Shield className="w-8 h-8" style={{ color: '#1D4ED8' }} />
            </div>
          </div>

          {success ? (
            // Success state
            <div className="text-center">
              <div 
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}
              >
                <Check className="w-7 h-7" style={{ color: '#10B981' }} />
              </div>
              <h2 className="text-lg font-semibold mb-2" style={{ color: '#001F3F' }}>
                Check your email for a secure sign in link
              </h2>
              <p className="text-sm" style={{ color: '#6B7280' }}>
                We sent a sign in link to <strong>{email}</strong>.
              </p>
            </div>
          ) : (
            // Sign in form
            <>
              <h2 className="text-xl font-bold text-center mb-3" style={{ color: '#001F3F' }}>
                Create your free VINdicated account
              </h2>
              <p className="text-sm text-center mb-1" style={{ color: '#6B7280' }}>
                We use your email to save your journey and keep your numbers in one place.
              </p>
              <p className="text-sm text-center mb-4" style={{ color: '#6B7280' }}>
                You also get 3 free car comparison reports.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
                    Email address
                  </label>
                  <div className="relative">
                    <Mail 
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5" 
                      style={{ color: '#9CA3AF' }} 
                    />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full pl-12 pr-4 py-3 rounded-xl border focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }}
                      required
                    />
                  </div>
                </div>

                {error && (
                  <p className="text-sm" style={{ color: '#EF4444' }}>
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 px-6 rounded-xl font-bold text-lg transition-all shadow-md"
                  style={{ 
                    backgroundColor: '#001F3F', 
                    color: '#FFFFFF',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                >
                  {isSubmitting ? 'Sending...' : 'Email me a secure link'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthGate;
